import { captureException } from '@sentry/nextjs';
import { capitalize } from 'lodash';
import toast from 'react-hot-toast';
import { useInsertPlayerReviewMutation } from 'types/generated/client';
import { useViewer } from 'hooks/useViewer';
import CloseIcon from 'svg/CloseIcon';
import { ButtonText } from 'components/Button';
import ReviewForm from 'components/ReviewForm';
import type { ReviewFormValues } from 'components/ReviewForm';
import Modal from 'components/modals/Modal';

const REVIEW_STAR_SRC = '/images/players/review-star.png';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  player: {
    id: string;
    name: string;
    profileImagePath?: string | null;
  };
  /** Fired after a successful write so the caller can refetch the reviews list. */
  onSubmitted?: () => void;
}

/**
 * Prop-driven, unlike the coach's `ModalSubmitReview`, which is mounted once in
 * `_app` and opens itself from query params. Here the profile owns the trigger and
 * has already gated on "played together and hasn't reviewed yet".
 */
export default function ModalSubmitPlayerReview({
  isOpen,
  closeModal,
  player,
  onSubmitted,
}: Props) {
  const viewer = useViewer();
  const [insertPlayerReview, { loading }] = useInsertPlayerReviewMutation();

  const handleSubmit = async ({ rating, review, isSkillLevelAccurate }: ReviewFormValues) => {
    if (!viewer.userId) return;

    try {
      await insertPlayerReview({
        variables: {
          playerUserId: player.id,
          reviewerUserId: viewer.userId,
          rating,
          review,
          isPlayerSkillLevelAccurate: isSkillLevelAccurate ?? null,
        },
      });
      toast.success('Thanks for the review!');
      closeModal();
      onSubmitted?.();
    } catch (error) {
      captureException(error);
      toast.error('Something went wrong submitting your review.');
    }
  };

  return (
    <Modal isOpen={isOpen} handleClose={closeModal} classNameMaxWidth="max-w-[40rem]">
      <div className="flex flex-row-reverse px-ds-2xl py-ds-xl">
        <ButtonText onClick={closeModal}>
          <CloseIcon className="h-4 w-4" />
        </ButtonText>
      </div>
      <ReviewForm
        media={<img className="h-[6.25rem] w-[6.25rem]" src={REVIEW_STAR_SRC} alt="" />}
        heading={`How was it playing with ${capitalize(player.name)}?`}
        placeholder={`Let the community know why they should play with ${capitalize(player.name)}`}
        showSkillAccuracy
        isSubmitting={loading}
        onSubmit={handleSubmit}
      />
    </Modal>
  );
}
